import { useState } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { useNavigate } from "react-router-dom";
import { useDevices, normalizeStatus } from "@/hooks/useDevices";
import { useQuery } from "@tanstack/react-query";
import * as policiesApi from "@/services/policiesApi";
import { apiGet } from "@/services/apiClient";
import {
  Monitor, Smartphone, Tablet, Activity, ShieldCheck, Settings2,
  ArrowUpRight, Loader2, Plus, Download,
} from "lucide-react";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle, GlassCardDescription } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OSDistributionChart } from "@/components/dashboard/OSDistributionChart";
import { DeviceStatusDistributionChart } from "@/components/dashboard/DeviceStatusDistributionChart";
import { PlatformBreakdownChart } from "@/components/dashboard/PlatformBreakdownChart";
import { RecentActivities } from "@/components/dashboard/RecentActivities";
import { EnrollDeviceDialog } from "@/components/device/EnrollDeviceDialog";
import { exportDevicesAsCsv } from "@/utils/exportDevices";
import { toast } from "sonner";
import { useCompactMode } from "@/hooks/useCompactMode";

const Dashboard = () => {
  const navigate = useNavigate();
  const { cx } = useCompactMode();
  const { data: devices = [], isLoading } = useDevices();
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);

  const { data: policies = [] } = useQuery({
    queryKey: ["policies"],
    queryFn: policiesApi.listPolicies,
    staleTime: 60000,
    retry: 1,
  });

  const counts = devices.reduce(
    (acc, d) => {
      const s = normalizeStatus(d.status);
      if (s === "active") acc.active++;
      else if (s === "inactive") acc.inactive++;
      else if (s === "pending") acc.pending++;
      acc.total++;
      return acc;
    },
    { active: 0, inactive: 0, pending: 0, total: 0 }
  );

  // Model distribution for top models — prefer human-readable product/modelName
  // over raw board IDs / Apple part numbers (e.g. "MG6A3LL").
  const modelCounts: Record<string, number> = {};
  devices.forEach((d) => {
    const model = d.product || d.modelName || d.model || "Unknown";
    modelCounts[model] = (modelCounts[model] || 0) + 1;
  });
  const topModels = Object.entries(modelCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const handleExport = () => {
    if (devices.length === 0) { toast.error("No devices to export"); return; }
    exportDevicesAsCsv(devices);
    toast.success(`Exported ${devices.length} devices to CSV`);
  };

  return (
    <div className="bg-background w-full">
      <div className={cx("w-full px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6", "w-full px-4 md:px-6 py-4 space-y-4")}>

        <PageHeader title="Dashboard" description="Fleet overview & device management">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button size="sm" onClick={() => setIsEnrollDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Enroll Device
          </Button>
        </PageHeader>

        {/* Status Cards */}
        <div className={cx("grid grid-cols-2 lg:grid-cols-4 gap-4", "grid grid-cols-2 lg:grid-cols-4 gap-3")}>
          <StatusCard
            label="Total Devices"
            value={counts.total}
            icon={<Monitor className="h-5 w-5" />}
            loading={isLoading}
            onClick={() => navigate("/devices")}
          />
          <StatusCard
            label="Active"
            value={counts.active}
            icon={<Activity className="h-5 w-5 text-success" />}
            loading={isLoading}
            accent="text-success"
            onClick={() => navigate("/devices")}
          />
          <StatusCard
            label="Inactive"
            value={counts.inactive}
            icon={<Smartphone className="h-5 w-5 text-orange-500" />}
            loading={isLoading}
            accent="text-orange-600"
            onClick={() => navigate("/devices")}
          />
          <StatusCard
            label="Configurations"
            value={policies.length}
            icon={<Settings2 className="h-5 w-5 text-primary" />}
            loading={false}
            onClick={() => navigate("/configurations")}
          />
        </div>

        {/* Charts Row 1 */}
        <div className={cx("grid gap-6 md:grid-cols-2 lg:grid-cols-3", "grid gap-4 md:grid-cols-2 lg:grid-cols-3")}>
          <GlassCard variant="default">
            <GlassCardHeader>
              <GlassCardTitle className="text-base font-medium">Device Status</GlassCardTitle>
              <GlassCardDescription className="text-xs">Current enrollment status</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="h-[200px] md:h-[260px]">

              {isLoading ? <ChartSkeleton /> : <DeviceStatusDistributionChart counts={counts} />}
            </GlassCardContent>
          </GlassCard>

          <GlassCard variant="default">
            <GlassCardHeader>
              <GlassCardTitle className="text-base font-medium">OS Distribution</GlassCardTitle>
              <GlassCardDescription className="text-xs">Operating systems across fleet</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="h-[200px] md:h-[260px]">
              {isLoading ? <ChartSkeleton /> : <OSDistributionChart devices={devices} />}
            </GlassCardContent>
          </GlassCard>

          <GlassCard variant="default">
            <GlassCardHeader>
              <GlassCardTitle className="text-base font-medium">Top Models</GlassCardTitle>
              <GlassCardDescription className="text-xs">Most common device models</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {isLoading ? (
                <ChartSkeleton />
              ) : topModels.length === 0 ? (
                <div className="flex items-center justify-center h-[260px] text-muted-foreground text-sm">No data</div>
              ) : (
                <div className="space-y-3 pt-2">
                  {topModels.map(([model, count]) => {
                    const pct = counts.total > 0 ? Math.round((count / counts.total) * 100) : 0;
                    return (
                      <div key={model} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate font-medium">{model}</span>
                          <span className="text-muted-foreground text-xs">{count} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Charts Row 2 */}
        <div className={cx("grid gap-6 md:grid-cols-2", "grid gap-4 md:grid-cols-2")}>
          <GlassCard variant="default">
            <GlassCardHeader>
              <GlassCardTitle className="text-base font-medium">Platform Breakdown</GlassCardTitle>
              <GlassCardDescription className="text-xs">Device status by operating system</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="h-[220px] md:h-[300px]">
              {isLoading ? <ChartSkeleton /> : <PlatformBreakdownChart devices={devices} />}
            </GlassCardContent>
          </GlassCard>

          <RecentActivities />
        </div>

        {/* Quick links */}
        <div className={cx("grid gap-4 md:grid-cols-3", "grid gap-3 md:grid-cols-3")}>
          <QuickLink title="Manage Devices" desc="View and manage all enrolled devices" onClick={() => navigate("/devices")} />
          <QuickLink title="Configurations" desc={`${policies.length} active configuration${policies.length !== 1 ? "s" : ""}`} onClick={() => navigate("/configurations")} />
          <QuickLink title="Tags" desc="Organize devices with tags" onClick={() => navigate("/tags")} />
        </div>

        <EnrollDeviceDialog isOpen={isEnrollDialogOpen} onClose={() => setIsEnrollDialogOpen(false)} />
      </div>
    </div>
  );
};

function StatusCard({ label, value, icon, loading, accent, subtitle, onClick }: {
  label: string; value: number; icon: React.ReactNode; loading: boolean; accent?: string; subtitle?: string; onClick?: () => void;
}) {
  const { cx } = useCompactMode();
  return (
    <GlassCard variant="default" hover onClick={onClick} className="cursor-pointer">
      <GlassCardContent className={cx("flex items-center justify-between p-4", "flex items-center justify-between p-3")}>
        <div>
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          {loading ? (
            <div className={cx("h-8 w-16 bg-muted rounded animate-pulse mt-1", "h-6 w-14 bg-muted rounded animate-pulse mt-1")} />
          ) : (
            <>
              <p className={cx(`text-2xl font-bold mt-1 ${accent || "text-foreground"}`, `text-xl font-bold mt-0.5 ${accent || "text-foreground"}`)}>{value}</p>
              {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{subtitle}</p>}
            </>
          )}
        </div>
        <div className={cx("p-2 bg-muted/50 rounded-lg", "p-1.5 bg-muted/50 rounded-md")}>{icon}</div>
      </GlassCardContent>
    </GlassCard>
  );
}

function QuickLink({ title, desc, onClick }: { title: string; desc: string; onClick: () => void }) {
  const { cx } = useCompactMode();
  return (
    <GlassCard variant="default" hover className="cursor-pointer" onClick={onClick}>
      <GlassCardContent className={cx("flex items-center justify-between p-4", "flex items-center justify-between p-3")}>
        <div>
          <p className="font-medium text-sm">{title}</p>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
      </GlassCardContent>
    </GlassCard>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export default Dashboard;
